import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Card, CardDescription, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Pagination } from '@/components/ui/Pagination';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { v2Api } from '@/lib/api';
import { resolveMediaUrl } from '@/lib/resolveMediaUrl';
import { canEndExerciseRun } from '@/lib/exerciseRunStatus';
import { stripHtml } from '@/lib/utils';
import {
  Trophy,
  StopCircle,
  Loader2,
  Users,
  Network,
  Router,
  Server,
  Clock,
  Eye,
  PlayCircle,
  Search,
} from 'lucide-react';

/** Exercise run lifecycle from Django ``/api/v2/scenarios/corporate/actives`` (and similar). */
export type ExerciseRunStatus =
  | 'participants_pending'
  | 'participants_added'
  | 'launching'
  | 'launched'
  | 'started'
  | 'paused'
  | 'completed'
  | 'cancelled'
  | 'failed'
  | string;

export interface ActiveScenarioItem {
  /** Relational Django list: run PK (number). Legacy Mongo rows: string id. */
  id: string | number;
  /** Custom label on ``ExerciseActiveModel`` (who started / session name). */
  name?: string;
  /** Run lifecycle; drives the status badge (not every row is "live"). */
  status?: ExerciseRunStatus;
  started_by: string;
  /** Run start (Django list); legacy rows may only have ``scenario.start_time``. */
  start_time?: string | null;
  scenario: {
    id: string;
    name: string;
    description: string;
    severity: string;
    category_name: string;
    thumbnail_url: string;
    type?: string;
    points?: number;
    start_time?: string;
  };
  total_participant: number;
  total_network: number;
  total_routers: number;
  total_instances: number;
}

interface ActivesResponse {
  results?: ActiveScenarioItem[];
  current_page?: number;
  next?: string | null;
  previous?: string | null;
  total_pages?: number;
}

function getDifficultyColor(difficulty: string) {
  switch (difficulty.toLowerCase()) {
    case 'very easy':
      return 'bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20';
    case 'medium':
      return 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20';
    case 'hard':
      return 'bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/20';
    case 'expert':
      return 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20';
    default:
      return 'bg-gray-500/10 text-gray-600 dark:text-gray-400 border-gray-500/20';
  }
}

function getTimeElapsed(startTime: string) {
  const start = new Date(startTime);
  const now = new Date();
  if (Number.isNaN(start.getTime())) return '—';
  const diff = Math.max(0, now.getTime() - start.getTime());
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

  if (days > 0) {
    return `${days}d ${hours}h ${minutes}m`;
  }
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m`;
  return '< 1m';
}

function getRunTimingDisplay(item: ActiveScenarioItem) {
  const status = (item.status ?? '').toLowerCase();
  const startTime = (item.start_time ?? item.scenario.start_time) as string | undefined;

  switch (status) {
    case 'started':
      return {
        creatorLabel: 'Started by',
        timingText: startTime ? `Running for ${getTimeElapsed(startTime)}` : 'Active',
        timingIconClass: 'text-emerald-500',
      };
    case 'paused':
      return {
        creatorLabel: 'Started by',
        timingText: startTime ? `Paused (ran for ${getTimeElapsed(startTime)})` : 'Paused',
        timingIconClass: 'text-amber-500',
      };
    case 'launched':
      return {
        creatorLabel: 'Launched by',
        timingText: 'Launched · Ready to start',
        timingIconClass: 'text-cyan-500',
      };
    case 'launching':
      return {
        creatorLabel: 'Initiated by',
        timingText: 'Provisioning infrastructure…',
        timingIconClass: 'text-blue-500',
      };
    case 'participants_added':
      return {
        creatorLabel: 'Configured by',
        timingText: 'Ready to launch',
        timingIconClass: 'text-sky-500',
      };
    case 'participants_pending':
      return {
        creatorLabel: 'Created by',
        timingText: 'Not started yet',
        timingIconClass: 'text-muted-foreground',
      };
    case 'completed':
      return {
        creatorLabel: 'Started by',
        timingText: 'Completed',
        timingIconClass: 'text-zinc-500',
      };
    case 'failed':
      return {
        creatorLabel: 'Initiated by',
        timingText: 'Launch failed',
        timingIconClass: 'text-destructive',
      };
    case 'cancelled':
      return {
        creatorLabel: 'Initiated by',
        timingText: 'Cancelled',
        timingIconClass: 'text-destructive',
      };
    default:
      if (startTime) {
        return {
          creatorLabel: 'Started by',
          timingText: `Running for ${getTimeElapsed(startTime)}`,
          timingIconClass: 'text-blue-500',
        };
      }
      return {
        creatorLabel: 'Created by',
        timingText: 'Not started yet',
        timingIconClass: 'text-muted-foreground',
      };
  }
}

function formatStatusLabel(status: string | undefined): string {
  if (!status) return 'Unknown';
  return status
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/** Badge for each run: only true "live" runs use the green pulsing treatment. */
function runStatusBadgeConfig(status: string | undefined) {
  const s = (status ?? '').toLowerCase();
  switch (s) {
    case 'started':
      return {
        label: 'Active',
        barClass: 'bg-green-500/90 text-white',
        pulse: true,
      };
    case 'paused':
      return {
        label: 'Paused',
        barClass: 'bg-amber-500/90 text-white',
        pulse: false,
      };
    case 'participants_pending':
      return {
        label: 'Participants pending',
        barClass: 'bg-slate-500/90 text-white',
        pulse: false,
      };
    case 'participants_added':
      return {
        label: 'Ready to launch',
        barClass: 'bg-sky-600/90 text-white',
        pulse: false,
      };
    case 'launching':
      return {
        label: 'Launching',
        barClass: 'bg-blue-600/90 text-white',
        pulse: true,
      };
    case 'launched':
      return {
        label: 'Launched',
        barClass: 'bg-cyan-600/90 text-white',
        pulse: false,
      };
    case 'completed':
      return {
        label: 'Completed',
        barClass: 'bg-zinc-500/90 text-white',
        pulse: false,
      };
    case 'cancelled':
      return {
        label: 'Cancelled',
        barClass: 'bg-red-600/90 text-white',
        pulse: false,
      };
    case 'failed':
      return {
        label: 'Failed',
        barClass: 'bg-red-700/90 text-white',
        pulse: false,
      };
    default:
      return {
        label: formatStatusLabel(status),
        barClass: 'bg-muted text-foreground border border-border',
        pulse: false,
      };
  }
}

export function ActiveScenarioList() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState<ActiveScenarioItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [nextPage, setNextPage] = useState<string | null>(null);
  const [prevPage, setPrevPage] = useState<string | null>(null);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [endingScenarioId, setEndingScenarioId] = useState<string | number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [, setTick] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => setTick((t) => t + 1), 30000);
    return () => clearInterval(timer);
  }, []);

  const page = searchParams.get('page');

  const createQueryString = useCallback(
    (name: string, value: string) => {
      const params = new URLSearchParams(searchParams.toString());
      params.set(name, value);
      return params.toString();
    },
    [searchParams]
  );

  useEffect(() => {
    const query = new URLSearchParams();
    if (page) query.set('page', page);

    setIsLoading(true);
    setError(null);
    (async () => {
      try {
        const paged = await v2Api.dashboard.scenarios.actives.list<ActiveScenarioItem>({
          page: page ?? undefined,
        });
        setData(Array.isArray(paged.results) ? paged.results : []);
        setCurrentPage(paged.current_page ?? 1);
        setNextPage(paged.next ?? null);
        setPrevPage(paged.previous ?? null);
        setTotalPages(paged.total_pages ?? 1);
      } catch (err) {
        console.error('Failed to fetch active scenarios:', err);
        setError(err instanceof Error ? err.message : 'Failed to load scenarios');
        setData([]);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [searchParams, page]);

  const filteredData = (data || []).filter((item) =>
    item.scenario.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handlePageClick = (url: string | null) => {
    if (!url) return;
    try {
      const parsedUrl = new URL(url);
      const pageParam = parsedUrl.searchParams.get('page');
      if (pageParam) {
        navigate(`/scenarios/actives?${createQueryString('page', pageParam)}`);
      }
    } catch {
      // ignore
    }
  };

  const pathname = '/scenarios/actives';

  const handleEndScenario = async (scenarioId: string | number) => {
    setEndingScenarioId(scenarioId);
    setError(null);
    try {
      await v2Api.dashboard.scenarios.actives.end(String(scenarioId));
      setData((prev) => prev.filter((item) => item.id !== scenarioId));
    } catch (err) {
      console.error('Error ending scenario:', err);
      setError(err instanceof Error ? err.message : 'Failed to end scenario');
    } finally {
      setEndingScenarioId(null);
    }
  };

  return (
    <>
      <div className="mb-8">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search active scenarios..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 h-11 bg-background border-2 focus:border-primary/50 transition-colors"
          />
        </div>
        {error && (
          <p className="mt-2 text-sm text-destructive">{error}</p>
        )}
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center py-12">
          <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="flex flex-col gap-5">
          {filteredData.length > 0 ? (
            <>
              <div className="grid gap-6 md:grid-cols-2 2xl:grid-cols-3">
                {filteredData.map((item) => {
                  const statusCfg = runStatusBadgeConfig(item.status);
                  return (
                  <Card
                    key={item.id}
                    className="group relative overflow-hidden border-2 transition-all duration-300 hover:shadow-2xl hover:border-primary/50 bg-gradient-to-br from-background to-muted/20"
                  >
                    <div
                      className={`absolute top-3 right-3 z-20 flex items-center gap-1.5 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-semibold shadow-lg ${statusCfg.barClass}`}
                    >
                      {statusCfg.pulse ? (
                        <span className="relative flex h-2 w-2">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
                          <span className="relative inline-flex rounded-full h-2 w-2 bg-white" />
                        </span>
                      ) : (
                        <span className="relative flex h-2 w-2 rounded-full bg-white/90" />
                      )}
                      {statusCfg.label}
                    </div>

                    <div className="relative w-full h-48 overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-900">
                      <img
                        src={resolveMediaUrl(item.scenario.thumbnail_url) || item.scenario.thumbnail_url}
                        alt={item.scenario.name}
                        className="object-cover w-full h-full transition-transform duration-500 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent z-10" />
                      <div className="absolute bottom-3 left-3 z-20 flex flex-wrap gap-2">
                        <Badge variant="secondary" className="text-xs font-normal border">
                          {item.scenario.category_name}
                        </Badge>
                        {item.scenario.type && (
                          <Badge variant="outline" className="bg-primary/90 text-primary-foreground border-primary backdrop-blur-sm">
                            {item.scenario.type}
                          </Badge>
                        )}
                        <Badge
                          variant="secondary"
                          className={`${getDifficultyColor(item.scenario.severity)} text-xs font-normal border backdrop-blur-sm`}
                        >
                          {item.scenario.severity}
                        </Badge>
                      </div>
                    </div>

                    <div className="p-5 space-y-4">
                      <div>
                        <CardTitle className="text-xl font-bold line-clamp-1 mb-1 group-hover:text-primary transition-colors">
                          {item.name?.trim() || item.scenario.name}
                        </CardTitle>
                        {item.name?.trim() && item.name.trim() !== item.scenario.name ? (
                          <p className="text-xs text-muted-foreground line-clamp-1 mb-2">
                            Exercise: {item.scenario.name}
                          </p>
                        ) : (
                          <div className="mb-2" />
                        )}
                        <CardDescription className="line-clamp-2 text-sm leading-relaxed">
                          {stripHtml(item.scenario.description)}
                        </CardDescription>
                      </div>

                      {(() => {
                        const timing = getRunTimingDisplay(item);
                        return (
                          <div className="space-y-2">
                            <div className="flex items-center gap-2 text-sm">
                              <div className="flex items-center gap-1.5 text-muted-foreground">
                                <PlayCircle className="h-4 w-4 text-primary" />
                                <span>{timing.creatorLabel} <strong className="text-foreground">{item.started_by}</strong></span>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <Clock className={`h-4 w-4 ${timing.timingIconClass}`} />
                              <span className="font-medium text-foreground">
                                {timing.timingText}
                              </span>
                            </div>
                          </div>
                        );
                      })()}

                      <div className="grid grid-cols-2 gap-3 p-3 bg-muted/50 dark:bg-muted/20 rounded-lg border border-border/50">
                        <div className="flex items-center gap-2">
                          <div className="p-1.5 bg-blue-500/10 rounded">
                            <Users className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Participants</p>
                            <p className="text-sm font-bold">{item.total_participant}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="p-1.5 bg-purple-500/10 rounded">
                            <Network className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Networks</p>
                            <p className="text-sm font-bold">{item.total_network}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="p-1.5 bg-orange-500/10 rounded">
                            <Router className="h-4 w-4 text-orange-600 dark:text-orange-400" />
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Routers</p>
                            <p className="text-sm font-bold">{item.total_routers}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="p-1.5 bg-green-500/10 rounded">
                            <Server className="h-4 w-4 text-green-600 dark:text-green-400" />
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Instances</p>
                            <p className="text-sm font-bold">{item.total_instances}</p>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center justify-center gap-2 p-2 bg-gradient-to-r from-yellow-500/10 to-orange-500/10 rounded-lg border border-yellow-500/20">
                        <Trophy className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
                        <span className="font-bold text-lg">{item.scenario.points ?? 0}</span>
                        <span className="text-sm text-muted-foreground">points available</span>
                      </div>

                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" className="flex-1 group/btn" asChild>
                          <Link
                            to={`/scenarios/active/${item.id}`}
                          >
                            <Eye className="h-4 w-4 mr-2 transition-transform group-hover/btn:scale-110" />
                            View Details
                          </Link>
                        </Button>
                        {canEndExerciseRun(item.status) && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="destructive"
                                size="sm"
                                className="flex-1"
                                disabled={endingScenarioId === item.id}
                              >
                                {endingScenarioId === item.id ? (
                                  <>
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    Ending...
                                  </>
                                ) : (
                                  <>
                                    <StopCircle className="h-4 w-4 mr-2" />
                                    End Scenario
                                  </>
                                )}
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  This will end the scenario &quot;{item.scenario.name}&quot; for all participants.
                                  This action cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel disabled={endingScenarioId === item.id}>
                                  Cancel
                                </AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleEndScenario(item.id)}
                                  disabled={endingScenarioId === item.id}
                                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                >
                                  {endingScenarioId === item.id ? (
                                    <>
                                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                      Ending...
                                    </>
                                  ) : (
                                    'End Scenario'
                                  )}
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                      </div>
                    </div>
                  </Card>
                  );
                })}
              </div>

              <Pagination
                totalPages={totalPages}
                currentPage={currentPage}
                onNext={() => handlePageClick(nextPage)}
                onPrev={() => handlePageClick(prevPage)}
                onFirst={() => navigate(`${pathname}?${createQueryString('page', '1')}`)}
                onLast={() => navigate(`${pathname}?${createQueryString('page', String(totalPages))}`)}
              />
            </>
          ) : (
            <div className="col-span-full text-center py-12">
              <div className="text-4xl mb-2">🔍</div>
              <h3 className="text-lg font-medium">No scenarios found</h3>
              <p className="text-muted-foreground">Try adjusting your search.</p>
            </div>
          )}
        </div>
      )}
    </>
  );
}
